import React, { useMemo } from 'react';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import Grid from 'lucide-react/dist/esm/icons/grid.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import HowToStepsChaseTheAceSection from './HowToStepsChaseTheAceSection';
import {
  HowToPlayStep1Title,
  HowToPlayStep1Desc,
  HowToPlayStep1Tip1,
  HowToPlayStep1Tip2,
  HowToPlayStep1Tip3,
  HowToPlayStep2Title,
  HowToPlayStep2Desc,
  HowToPlayStep2Tip1,
  HowToPlayStep2Tip2,
  HowToPlayStep2Tip3,
  HowToPlayStep3Title,
  HowToPlayStep3Desc,
  HowToPlayStep3Tip1,
  HowToPlayStep3Tip2,
  HowToPlayStep3Tip3,
  HowToPlayStep4Title,
  HowToPlayStep4Desc,
  HowToPlayStep4Tip1,
  HowToPlayStep4Tip2,
  HowToPlayStep4Tip3,
} from '../../../content/keys';

const FALLBACKS = {
  [HowToPlayStep1Title]: 'Sign Up',
  [HowToPlayStep1Desc]: 'Create your free account in just a few minutes. All you need is your email address to get started.',
  [HowToPlayStep1Tip1]: 'Quick registration',
  [HowToPlayStep1Tip2]: 'Secure account',
  [HowToPlayStep1Tip3]: 'Free to join',
  [HowToPlayStep2Title]: 'Buy Tickets',
  [HowToPlayStep2Desc]: 'Purchase your tickets using our secure checkout. Multiple payment options available.',
  [HowToPlayStep2Tip1]: 'Secure payments',
  [HowToPlayStep2Tip2]: 'Instant confirmation',
  [HowToPlayStep2Tip3]: 'Multiple options',
  [HowToPlayStep3Title]: 'Watch the Draw',
  [HowToPlayStep3Desc]: 'Follow the draw results to see if your ticket number is selected. Every entry supports the cause!',
  [HowToPlayStep3Tip1]: 'Certified draws',
  [HowToPlayStep3Tip2]: 'Fair play guaranteed',
  [HowToPlayStep3Tip3]: 'Official results',
  [HowToPlayStep4Title]: 'Collect Winnings',
  [HowToPlayStep4Desc]: 'Winners are contacted directly by email and phone to arrange official prize payout.',
  [HowToPlayStep4Tip1]: 'Direct notification',
  [HowToPlayStep4Tip2]: 'Verified payouts',
  [HowToPlayStep4Tip3]: 'Official winner announcement',
};

const GUEST_FALLBACKS = {
  step1Title: 'Choose Your Tickets',
  step1Desc: 'Select your preferred ticket package. No account registration is required to play!',
  step1Tip1: 'Quick selection',
  step1Tip2: 'No account needed',
  step1Tip3: 'Multiple packages',
  step2Title: 'Secure Checkout',
  step2Desc: 'Complete your purchase as a guest with instant email confirmation.',
  step2Tip1: 'Secure payments',
  step2Tip2: 'Instant confirmation',
  step2Tip3: 'Email receipt',
};

function getGameQuickSteps(gameType, isGuest) {
  const prefix = `howToPlay.steps.${gameType || 'classic5050'}`;

  if (gameType === 'squareGame') {
    return [
      {
        icon: Grid,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'Pick Your Square',
        fallbackDesc: 'Tap any available square number on the live board to reserve it for checkout.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: 'Live availability' },
          { key: `${prefix}.step1Tip2`, fallback: 'Temporary hold timer' },
          { key: `${prefix}.step1Tip3`, fallback: 'Pick your lucky number' },
        ],
      },
      {
        icon: CreditCard,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Lock In Your Square',
        fallbackDesc: isGuest
          ? 'Checkout as a guest before your hold timer expires — no account needed.'
          : 'Complete payment securely before your hold timer expires to claim the square.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: 'Secure checkout' },
          { key: `${prefix}.step2Tip2`, fallback: 'Instant email receipt' },
          { key: `${prefix}.step2Tip3`, fallback: isGuest ? 'No account needed' : 'Saved to My Squares' },
        ],
      },
      {
        icon: Trophy,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Winning Square Drawn',
        fallbackDesc: 'Once sales close or the board fills, the winning square number is drawn.',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Certified random draw' },
          { key: `${prefix}.step3Tip2`, fallback: 'Real-time board updates' },
          { key: `${prefix}.step3Tip3`, fallback: 'Transparent results' },
        ],
      },
      {
        icon: CheckCircle,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Claim Your Prize',
        fallbackDesc: 'The owner of the winning square is contacted directly to receive the prize!',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Direct winner contact' },
          { key: `${prefix}.step4Tip2`, fallback: 'Fast prize payout' },
          { key: `${prefix}.step4Tip3`, fallback: 'Supports local charity' },
        ],
      },
    ];
  }

  if (gameType === 'superballBingo') {
    return [
      {
        icon: Ticket,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'Choose Your Tickets',
        fallbackDesc: 'Select your preferred ticket bundle for the current Superball Bingo session.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: 'Value ticket bundles' },
          { key: `${prefix}.step1Tip2`, fallback: isGuest ? 'No account needed' : 'Quick selection' },
          { key: `${prefix}.step1Tip3`, fallback: 'Multiple entry options' },
        ],
      },
      {
        icon: Sparkles,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Pick Your Superball',
        fallbackDesc: 'Choose a Superball number from 1 to 75 (or have one assigned) and checkout securely.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: 'Numbers 1 to 75' },
          { key: `${prefix}.step2Tip2`, fallback: 'Secure checkout' },
          { key: `${prefix}.step2Tip3`, fallback: 'Instant email receipt' },
        ],
      },
      {
        icon: Trophy,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Session & Superball Draw',
        fallbackDesc: 'One ticket wins the session prize — and if your Superball matches, you win the Jackpot!',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Guaranteed session prize' },
          { key: `${prefix}.step3Tip2`, fallback: 'Golden Superball bonus' },
          { key: `${prefix}.step3Tip3`, fallback: 'Rollover jackpot' },
        ],
      },
      {
        icon: CheckCircle,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Collect Winnings',
        fallbackDesc: 'Winners are notified automatically by email and phone for prize verification.',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Direct notification' },
          { key: `${prefix}.step4Tip2`, fallback: 'Verified payouts' },
          { key: `${prefix}.step4Tip3`, fallback: 'Supports the cause' },
        ],
      },
    ];
  }

  if (gameType === 'eliminationDraw') {
    return [
      {
        icon: Ticket,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'Get Your Entries',
        fallbackDesc: 'Choose your ticket package — every ticket gives you another chance to survive the draw.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: 'Multiple entry bundles' },
          { key: `${prefix}.step1Tip2`, fallback: isGuest ? 'No account needed' : 'Fast selection' },
          { key: `${prefix}.step1Tip3`, fallback: 'Supports the charity' },
        ],
      },
      {
        icon: CreditCard,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Secure Checkout',
        fallbackDesc: 'Complete your purchase online and receive your official ticket numbers by email.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: 'Secure payments' },
          { key: `${prefix}.step2Tip2`, fallback: 'Instant confirmation' },
          { key: `${prefix}.step2Tip3`, fallback: 'Email ticket receipt' },
        ],
      },
      {
        icon: Trophy,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Reverse Elimination',
        fallbackDesc: 'Tickets are drawn one by one with milestone prizes awarded along the way.',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Milestone prizes' },
          { key: `${prefix}.step3Tip2`, fallback: 'Reverse draw suspense' },
          { key: `${prefix}.step3Tip3`, fallback: 'Certified fair draw' },
        ],
      },
      {
        icon: CheckCircle,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Last Ticket Wins',
        fallbackDesc: 'The very last ticket remaining in the draw takes home the Grand Prize!',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Grand Prize winner' },
          { key: `${prefix}.step4Tip2`, fallback: 'Direct winner contact' },
          { key: `${prefix}.step4Tip3`, fallback: 'Official payout' },
        ],
      },
    ];
  }

  if (gameType === 'modified5050') {
    return [
      {
        icon: Ticket,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'Select Your Package',
        fallbackDesc: 'Choose your 50/50 ticket bundle to enter the draw.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: '50% to the winner' },
          { key: `${prefix}.step1Tip2`, fallback: isGuest ? 'No account needed' : 'Quick selection' },
          { key: `${prefix}.step1Tip3`, fallback: 'Great value bundles' },
        ],
      },
      {
        icon: Calendar,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Choose Your Weeks',
        fallbackDesc: 'Play the current draw or select multiple consecutive weekly draws in advance.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: 'Single or multi-week' },
          { key: `${prefix}.step2Tip2`, fallback: 'Never miss a draw' },
          { key: `${prefix}.step2Tip3`, fallback: 'One easy checkout' },
        ],
      },
      {
        icon: CreditCard,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Secure Checkout',
        fallbackDesc: 'Complete payment securely and receive your official ticket numbers by email.',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Secure payments' },
          { key: `${prefix}.step3Tip2`, fallback: 'Instant email receipt' },
          { key: `${prefix}.step3Tip3`, fallback: 'Auto-entered each week' },
        ],
      },
      {
        icon: Trophy,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Weekly 50/50 Draws',
        fallbackDesc: 'Each draw awards 50% of that draw pot to the winning ticket holder!',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Certified random draw' },
          { key: `${prefix}.step4Tip2`, fallback: 'Direct winner contact' },
          { key: `${prefix}.step4Tip3`, fallback: 'Supports local causes' },
        ],
      },
    ];
  }

  if (gameType === 'progressive5050') {
    return [
      {
        icon: Ticket,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'Choose Your Tickets',
        fallbackDesc: 'Select your ticket bundle to enter the Progressive 50/50 draw.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: 'Multiple bundle options' },
          { key: `${prefix}.step1Tip2`, fallback: isGuest ? 'No account needed' : 'Quick selection' },
          { key: `${prefix}.step1Tip3`, fallback: 'Supports the cause' },
        ],
      },
      {
        icon: CreditCard,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Secure Checkout',
        fallbackDesc: 'Complete your purchase online and receive your official ticket numbers by email.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: 'Secure payments' },
          { key: `${prefix}.step2Tip2`, fallback: 'Instant confirmation' },
          { key: `${prefix}.step2Tip3`, fallback: 'Official email receipt' },
        ],
      },
      {
        icon: TrendingUp,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Growing Progressive Pot',
        fallbackDesc: 'Every ticket builds the jackpot — if not won, the progressive pot rolls over!',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Rollover jackpots' },
          { key: `${prefix}.step3Tip2`, fallback: 'Certified random draw' },
          { key: `${prefix}.step3Tip3`, fallback: 'Bigger prizes each cycle' },
        ],
      },
      {
        icon: CheckCircle,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Collect Winnings',
        fallbackDesc: 'Winners are contacted directly by email and phone to claim their cash prize.',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Direct notification' },
          { key: `${prefix}.step4Tip2`, fallback: 'Verified payouts' },
          { key: `${prefix}.step4Tip3`, fallback: 'Official results posted' },
        ],
      },
    ];
  }

  if (gameType === 'prizeDraw') {
    return [
      {
        icon: Gift,
        titleKey: `${prefix}.step1Title`,
        descKey: `${prefix}.step1Desc`,
        fallbackTitle: 'View Featured Prizes',
        fallbackDesc: 'Check out the Grand Prize and any Early Bird prizes up for grabs.',
        tipKeys: [
          { key: `${prefix}.step1Tip1`, fallback: 'Exciting Grand Prizes' },
          { key: `${prefix}.step1Tip2`, fallback: 'Early Bird draws' },
          { key: `${prefix}.step1Tip3`, fallback: 'Supports local charity' },
        ],
      },
      {
        icon: Ticket,
        titleKey: `${prefix}.step2Title`,
        descKey: `${prefix}.step2Desc`,
        fallbackTitle: 'Select Your Tickets',
        fallbackDesc: 'Choose your ticket bundle and complete checkout with instant email confirmation.',
        tipKeys: [
          { key: `${prefix}.step2Tip1`, fallback: isGuest ? 'No account needed' : 'Multiple bundles' },
          { key: `${prefix}.step2Tip2`, fallback: 'Secure checkout' },
          { key: `${prefix}.step2Tip3`, fallback: 'Email ticket delivery' },
        ],
      },
      {
        icon: Trophy,
        titleKey: `${prefix}.step3Title`,
        descKey: `${prefix}.step3Desc`,
        fallbackTitle: 'Official Prize Draw',
        fallbackDesc: 'Winning ticket numbers are drawn on the scheduled draw dates.',
        tipKeys: [
          { key: `${prefix}.step3Tip1`, fallback: 'Certified random draw' },
          { key: `${prefix}.step3Tip2`, fallback: 'Fair play guaranteed' },
          { key: `${prefix}.step3Tip3`, fallback: 'Published winners list' },
        ],
      },
      {
        icon: CheckCircle,
        titleKey: `${prefix}.step4Title`,
        descKey: `${prefix}.step4Desc`,
        fallbackTitle: 'Claim Your Prize',
        fallbackDesc: 'Winners are contacted directly by phone and email to arrange prize delivery.',
        tipKeys: [
          { key: `${prefix}.step4Tip1`, fallback: 'Direct notification' },
          { key: `${prefix}.step4Tip2`, fallback: 'Guided prize claim' },
          { key: `${prefix}.step4Tip3`, fallback: 'Official verification' },
        ],
      },
    ];
  }

  return null;
}

export default function HowToStepsSection({ section }) {
  const { gameType } = useGameType();
  if (gameType === 'chaseTheAce') {
    return <HowToStepsChaseTheAceSection section={section} />;
  }

  const { isGuestCheckoutEnabled } = useAuthConfig();
  const isGuest = isGuestCheckoutEnabled();
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const t = (key, fallback) => {
    const v = c(key);
    if (v) return v;
    const trVal = tr(key);
    if (trVal && trVal !== key) return trVal;
    return fallback ?? FALLBACKS[key] ?? '';
  };

  const gameSpecificSteps = useMemo(
    () => getGameQuickSteps(gameType, isGuest),
    [gameType, isGuest]
  );

  const steps = gameSpecificSteps || (isGuest ? [
    {
      icon: Ticket,
      titleKey: 'HowToPlayStep1GuestTitle',
      descKey: 'HowToPlayStep1GuestDesc',
      fallbackTitle: GUEST_FALLBACKS.step1Title,
      fallbackDesc: GUEST_FALLBACKS.step1Desc,
      tipKeys: [
        { key: 'HowToPlayStep1GuestTip1', fallback: GUEST_FALLBACKS.step1Tip1 },
        { key: 'HowToPlayStep1GuestTip2', fallback: GUEST_FALLBACKS.step1Tip2 },
        { key: 'HowToPlayStep1GuestTip3', fallback: GUEST_FALLBACKS.step1Tip3 },
      ]
    },
    {
      icon: CreditCard,
      titleKey: 'HowToPlayStep2GuestTitle',
      descKey: 'HowToPlayStep2GuestDesc',
      fallbackTitle: GUEST_FALLBACKS.step2Title,
      fallbackDesc: GUEST_FALLBACKS.step2Desc,
      tipKeys: [
        { key: 'HowToPlayStep2GuestTip1', fallback: GUEST_FALLBACKS.step2Tip1 },
        { key: 'HowToPlayStep2GuestTip2', fallback: GUEST_FALLBACKS.step2Tip2 },
        { key: 'HowToPlayStep2GuestTip3', fallback: GUEST_FALLBACKS.step2Tip3 },
      ]
    },
    {
      icon: Trophy,
      titleKey: HowToPlayStep3Title,
      descKey: HowToPlayStep3Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep3Title],
      fallbackDesc: FALLBACKS[HowToPlayStep3Desc],
      tipKeys: [
        { key: HowToPlayStep3Tip1, fallback: FALLBACKS[HowToPlayStep3Tip1] },
        { key: HowToPlayStep3Tip2, fallback: FALLBACKS[HowToPlayStep3Tip2] },
        { key: HowToPlayStep3Tip3, fallback: FALLBACKS[HowToPlayStep3Tip3] },
      ]
    },
    {
      icon: CheckCircle,
      titleKey: HowToPlayStep4Title,
      descKey: HowToPlayStep4Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep4Title],
      fallbackDesc: FALLBACKS[HowToPlayStep4Desc],
      tipKeys: [
        { key: HowToPlayStep4Tip1, fallback: FALLBACKS[HowToPlayStep4Tip1] },
        { key: HowToPlayStep4Tip2, fallback: FALLBACKS[HowToPlayStep4Tip2] },
        { key: HowToPlayStep4Tip3, fallback: FALLBACKS[HowToPlayStep4Tip3] },
      ]
    }
  ] : [
    {
      icon: UserPlus,
      titleKey: HowToPlayStep1Title,
      descKey: HowToPlayStep1Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep1Title],
      fallbackDesc: FALLBACKS[HowToPlayStep1Desc],
      tipKeys: [
        { key: HowToPlayStep1Tip1, fallback: FALLBACKS[HowToPlayStep1Tip1] },
        { key: HowToPlayStep1Tip2, fallback: FALLBACKS[HowToPlayStep1Tip2] },
        { key: HowToPlayStep1Tip3, fallback: FALLBACKS[HowToPlayStep1Tip3] },
      ]
    },
    {
      icon: CreditCard,
      titleKey: HowToPlayStep2Title,
      descKey: HowToPlayStep2Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep2Title],
      fallbackDesc: FALLBACKS[HowToPlayStep2Desc],
      tipKeys: [
        { key: HowToPlayStep2Tip1, fallback: FALLBACKS[HowToPlayStep2Tip1] },
        { key: HowToPlayStep2Tip2, fallback: FALLBACKS[HowToPlayStep2Tip2] },
        { key: HowToPlayStep2Tip3, fallback: FALLBACKS[HowToPlayStep2Tip3] },
      ]
    },
    {
      icon: Trophy,
      titleKey: HowToPlayStep3Title,
      descKey: HowToPlayStep3Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep3Title],
      fallbackDesc: FALLBACKS[HowToPlayStep3Desc],
      tipKeys: [
        { key: HowToPlayStep3Tip1, fallback: FALLBACKS[HowToPlayStep3Tip1] },
        { key: HowToPlayStep3Tip2, fallback: FALLBACKS[HowToPlayStep3Tip2] },
        { key: HowToPlayStep3Tip3, fallback: FALLBACKS[HowToPlayStep3Tip3] },
      ]
    },
    {
      icon: CheckCircle,
      titleKey: HowToPlayStep4Title,
      descKey: HowToPlayStep4Desc,
      fallbackTitle: FALLBACKS[HowToPlayStep4Title],
      fallbackDesc: FALLBACKS[HowToPlayStep4Desc],
      tipKeys: [
        { key: HowToPlayStep4Tip1, fallback: FALLBACKS[HowToPlayStep4Tip1] },
        { key: HowToPlayStep4Tip2, fallback: FALLBACKS[HowToPlayStep4Tip2] },
        { key: HowToPlayStep4Tip3, fallback: FALLBACKS[HowToPlayStep4Tip3] },
      ]
    }
  ]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-6 md:gap-8 mb-8 sm:mb-16 md:mb-20">
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-md sm:shadow-lg border border-gray-200 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              {/* Header: Step Pill on Left, Icon on Right */}
              <div className="flex items-center justify-between gap-2 mb-3.5">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-charity-primary/10 text-charity-primary border border-charity-primary/20">
                  Step {index + 1}
                </span>
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
                >
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>

              <InlineTranslationEdit
                translationKey={step.titleKey}
                value={t(step.titleKey, step.fallbackTitle)}
                fallback={step.fallbackTitle}
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
                className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1.5 block text-left"
              />
              <InlineTranslationEdit
                translationKey={step.descKey}
                value={t(step.descKey, step.fallbackDesc)}
                fallback={step.fallbackDesc}
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
                className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed block text-left"
              />
            </div>

            {step.tipKeys && step.tipKeys.length > 0 && (
              <div className="w-full space-y-1.5 mt-3.5 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                {step.tipKeys.map((tipItem, tipIndex) => (
                  <div key={tipIndex} className="flex items-start gap-2 text-left">
                    <CheckCircle className="w-3.5 h-3.5 text-charity-primary flex-shrink-0 mt-0.5" />
                    <InlineTranslationEdit
                      translationKey={tipItem.key}
                      value={t(tipItem.key, tipItem.fallback)}
                      fallback={tipItem.fallback}
                      editMode={editMode}
                      onUpdate={handleTranslationUpdate}
                      className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
